/**
 * Sel-2 hydration timeline — syncs active step with main media (autoplay + click).
 */
(function () {
  'use strict';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function toNumber(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function Sel2HydrationTimeline(root) {
    this.root = root;
    this.steps = qsa(root, '[data-sel-2-ht-step]');
    this.slides = qsa(root, '[data-sel-2-ht-slide]');
    this.thumbs = qsa(root, '[data-sel-2-ht-thumbs]');
    this.autoplayBtn = qs(root, '[data-sel-2-ht-autoplay-toggle]');
    this.prevBtn = qs(root, '[data-sel-2-ht-prev]');
    this.nextBtn = qs(root, '[data-sel-2-ht-next]');
    this.index = 0;
    this.timer = null;
    this.userPaused = false;
    this.interval = Math.max(2000, toNumber(root.getAttribute('data-autoplay-ms'), 5000));
    this.autoplay = root.getAttribute('data-autoplay') !== 'false';

    if (!this.steps.length || !this.slides.length) return;

    this.bind();
    this.setActive(0, false);
    this.syncAutoplayButton();
    if (this.autoplay && !this.userPaused) this.start();
  }

  Sel2HydrationTimeline.prototype.bind = function () {
    var self = this;

    this.steps.forEach(function (step, i) {
      step.addEventListener('click', function () {
        self.setActive(i, true);
        self.restart();
      });
      step.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self.setActive(i, true);
          self.restart();
        }
      });
    });

    if (this.autoplayBtn) {
      this.autoplayBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.toggleAutoplay();
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.setActive(self.index - 1, true);
        self.restart();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.setActive(self.index + 1, true);
        self.restart();
      });
    }

    qsa(this.root, '[data-sel-2-ht-mute]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        self.toggleMute(btn);
      });
    });

    qsa(this.root, '[data-sel-2-ht-play]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        self.toggleVideoPlay(btn);
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.stop();
      } else if (self.autoplay && !self.userPaused) {
        self.start();
      }
    });
  };

  Sel2HydrationTimeline.prototype.toggleAutoplay = function () {
    this.userPaused = !this.userPaused;
    if (this.userPaused) {
      this.stop();
    } else if (this.autoplay) {
      this.start();
    }
    this.syncAutoplayButton();
  };

  Sel2HydrationTimeline.prototype.syncAutoplayButton = function () {
    if (!this.autoplayBtn) return;
    var playing = this.autoplay && !this.userPaused;
    this.autoplayBtn.classList.toggle('is-playing', playing);
    this.autoplayBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    this.autoplayBtn.setAttribute('aria-label', playing ? 'Pause timeline' : 'Play timeline');
  };

  Sel2HydrationTimeline.prototype.setActive = function (index, userInitiated) {
    var count = Math.min(this.steps.length, this.slides.length);
    if (!count) return;
    this.index = ((index % count) + count) % count;

    this.steps.forEach(function (step, i) {
      var active = i === this.index;
      step.classList.toggle('is-active', active);
      step.setAttribute('aria-current', active ? 'true' : 'false');
      step.setAttribute('aria-selected', active ? 'true' : 'false');
      step.tabIndex = active ? 0 : -1;
    }, this);

    this.slides.forEach(function (slide, i) {
      var active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      this.syncMedia(slide, active, userInitiated);
    }, this);

    this.thumbs.forEach(function (thumbSet, i) {
      var active = i === this.index;
      thumbSet.classList.toggle('is-active', active);
      thumbSet.setAttribute('aria-hidden', active ? 'false' : 'true');
    }, this);
  };

  Sel2HydrationTimeline.prototype.syncMedia = function (slide, active, userInitiated) {
    var video = qs(slide, 'video');
    if (!video) return;

    if (active) {
      if (userInitiated || (this.autoplay && !this.userPaused)) {
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
      }
      this.updateVideoPlayButton(slide, !video.paused);
    } else {
      video.pause();
      if (!isNaN(video.duration)) video.currentTime = 0;
      this.updateVideoPlayButton(slide, false);
    }
  };

  Sel2HydrationTimeline.prototype.updateVideoPlayButton = function (slide, playing) {
    var btn = qs(slide, '[data-sel-2-ht-play]');
    if (!btn) return;
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    btn.classList.toggle('is-playing', playing);
    btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  };

  Sel2HydrationTimeline.prototype.toggleMute = function (btn) {
    var slide = btn.closest('[data-sel-2-ht-slide]');
    var video = slide ? qs(slide, 'video') : null;
    if (!video) return;
    video.muted = !video.muted;
    btn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
    btn.classList.toggle('is-muted', video.muted);
    btn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
  };

  Sel2HydrationTimeline.prototype.toggleVideoPlay = function (btn) {
    var slide = btn.closest('[data-sel-2-ht-slide]');
    var video = slide ? qs(slide, 'video') : null;
    if (!video) return;
    if (video.paused) {
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
      this.updateVideoPlayButton(slide, true);
    } else {
      video.pause();
      this.updateVideoPlayButton(slide, false);
    }
  };

  Sel2HydrationTimeline.prototype.next = function () {
    this.setActive(this.index + 1, false);
  };

  Sel2HydrationTimeline.prototype.start = function () {
    var self = this;
    this.stop();
    if (!this.autoplay || this.userPaused) return;
    this.timer = window.setInterval(function () {
      self.next();
    }, this.interval);
  };

  Sel2HydrationTimeline.prototype.stop = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  Sel2HydrationTimeline.prototype.restart = function () {
    this.stop();
    if (this.autoplay && !this.userPaused) this.start();
  };

  function initAll() {
    qsa(document, '[data-sel-2-hydration-timeline]').forEach(function (root) {
      if (root.getAttribute('data-sel-2-ht-ready') === '1') return;
      root.setAttribute('data-sel-2-ht-ready', '1');
      new Sel2HydrationTimeline(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
